import { useEffect, Suspense, lazy } from 'react';
import { Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';
import { HelmetProvider, Helmet } from 'react-helmet-async';
import { ThemeProvider } from '@mui/material/styles';
import { CircularProgress } from '@mui/material';
import Cookies from 'js-cookie';
import { theme } from './theme/theme';
import {
  Login,
  Register,
  Home,
  NotFound,
} from './pages';
import { GlobalLoader } from './components/atoms/GlobalLoader/GlobalLoader';
import { ErrorBoundary } from './components/atoms/ErrorBoundary';
import { loadMe, logInUserWithOauth } from './store/auth/actions';
import { trackPageView } from './utils/analytics';
import { useBfcacheCleanup } from './hooks/useBfcacheCleanup';

const pageFallback = (
  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
    <CircularProgress size={24} color="inherit" />
  </div>
);

function LazyPage({ children }) {
  return (
    <ErrorBoundary>
      <Suspense fallback={pageFallback}>{children}</Suspense>
    </ErrorBoundary>
  );
}

// Lazy load non-critical pages
const LazyForgotPass = lazy(() => import('./pages/ForgotPass').then(m => ({ default: m.ForgotPass })));
const LazyPassChange = lazy(() => import('./pages/PassChange').then(m => ({ default: m.PassChange })));
const LazyIdentifyVerification = lazy(() => import('./pages/IdentifyVerification').then(m => ({ default: m.IdentifyVerification })));
const LazyPrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy').then(m => ({ default: m.PrivacyPolicy })));
const LazyTermsOfService = lazy(() => import('./pages/TermsOfService').then(m => ({ default: m.TermsOfService })));
const LazyAdmin = lazy(() => import('./pages/Admin').then(m => ({ default: m.Admin })));
const LazyAdminComments = lazy(() => import('./pages/Admin/AdminComments').then(m => ({ default: m.AdminComments })));
const LazyAdminUsers = lazy(() => import('./pages/Admin/AdminUsers').then(m => ({ default: m.AdminUsers })));
const LazyProfile = lazy(() => import('./pages/Profile').then(m => ({ default: m.Profile })));
const LazyAbout = lazy(() => import('./pages/About').then(m => ({ default: m.About })));
const LazyEditPlace = lazy(() => import('./pages/EditPlace').then(m => ({ default: m.EditPlace })));

function UsersRoutes() {
  return (
    <>
      <Helmet><meta name="robots" content="noindex, nofollow" /></Helmet>
      <Routes>
        <Route path="login" element={<Login />} />
        <Route path="register" element={<Register />} />
        <Route path="confirm/:id/:token" element={<LazyPage><LazyPassChange /></LazyPage>} />
        <Route path="forgot" element={<LazyPage><LazyForgotPass /></LazyPage>} />
        <Route path="identity_verification" element={<LazyPage><LazyIdentifyVerification /></LazyPage>} />
      </Routes>
    </>
  );
}

const App = ({ logInUserWithOauth, auth, loadMe }) => {
  const location = useLocation();
  const navigate = useNavigate();

  // Enable bfcache-friendly cleanup for WebSocket connections
  useBfcacheCleanup();

  // Map routes to page titles
  const getPageTitle = () => {
    const path = location.pathname;
    
    if (path === '/') return 'Home';
    if (path === '/about') return 'About';
    if (path === '/users/login') return 'Login';
    if (path === '/users/register') return 'Register';
    if (path === '/users/forgot') return 'Forgot Password';
    if (path.startsWith('/users/confirm')) return 'Reset Password';
    if (path === '/users/identity_verification') return 'Identity Verification';
    if (path === '/profile') return 'Profile';
    if (path === '/admin') return 'Admin';
    if (path === '/edit' || path.startsWith('/edit/')) return 'Edit Place';
    if (path === '/privacy-policy') return 'Privacy Policy';
    if (path === '/terms-of-service') return 'Terms of Service';
    if (path.startsWith('/places/')) return 'Place';

    return 'Tuksums';
  };

  useEffect(() => {
    const pageTitle = getPageTitle();
    const fullTitle = pageTitle === 'Tuksums' ? 'Tuksums' : `${pageTitle} - Tuksums`;
    document.title = fullTitle;
    trackPageView(location.pathname, fullTitle);
  }, [location]);

  useEffect(() => {
    if (window.location.hash === '#_=_') window.location.hash = '';

    const cookieJwt = Cookies.get('x-auth-cookie');
    if (cookieJwt) {
      Cookies.remove('x-auth-cookie');
      logInUserWithOauth(cookieJwt, navigate);
    }
  }, []);

  useEffect(() => {
    if (!auth.appLoaded && !auth.isLoading && auth.token && !auth.isAuthenticated) {
       loadMe();
    }
  }, []);

  return (
    <GoogleReCaptchaProvider
      reCaptchaKey={process.env.REACT_APP_RECAPTCHA_KEY}
      scriptProps={{ async: true, defer: true, appendTo: 'head' }}
    >
    <ThemeProvider theme={theme}>
      <HelmetProvider>
        <>
          <GlobalLoader />
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/places/:id" element={<Home />} />
            <Route path="/profile" element={<LazyPage><LazyProfile /></LazyPage>} />
            <Route path="/admin" element={<LazyPage><LazyAdmin /></LazyPage>} />
            <Route path="/admin/comments" element={<LazyPage><LazyAdminComments /></LazyPage>} />
            <Route path="/admin/users" element={<LazyPage><LazyAdminUsers /></LazyPage>} />
            <Route path="/privacy-policy" element={<LazyPage><LazyPrivacyPolicy /></LazyPage>} />
            <Route path="/terms-of-service" element={<LazyPage><LazyTermsOfService /></LazyPage>} />
            <Route path="/about" element={<LazyPage><LazyAbout /></LazyPage>} />
            <Route path="/edit" element={<LazyPage><LazyEditPlace /></LazyPage>} />
            <Route path="/edit/:id" element={<LazyPage><LazyEditPlace /></LazyPage>} />
            <Route path="/users/*" element={<UsersRoutes />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </>
      </HelmetProvider>
    </ThemeProvider>
    </GoogleReCaptchaProvider>
  );
};

const mapStateToProps = (state) => ({
  auth: state.auth,
});

export default compose(connect(mapStateToProps, { logInUserWithOauth, loadMe }))(App);
